import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../services/api';
import {
  HiOutlineRefresh,
  HiOutlineVideoCamera,
  HiOutlineCollection,
  HiOutlineUsers,
  HiOutlineFilm,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

function AdminSeries() {
  const { t } = useTranslation();
  const [keyStatus, setKeyStatus] = useState({ configured: false });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncingCurrent, setSyncingCurrent] = useState(0);
  const [syncingTotal, setSyncingTotal] = useState(0);
  const [syncingTitle, setSyncingTitle] = useState('');
  const [eventSource, setEventSource] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [keyRes, statsRes] = await Promise.all([
        settingsAPI.getTMDBKeyStatus(),
        settingsAPI.getStats(),
      ]);
      setKeyStatus(keyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTVShows = async () => {
    if (!keyStatus.configured) {
      toast.error(t('dashboard.configureApiKey'));
      return;
    }

    setSyncing(true);
    setSyncProgress(0);
    setSyncingCurrent(0);
    setSyncingTotal(0);
    setSyncingTitle(t('common.initializing'));

    try {
      const connection = settingsAPI.syncTVShowsStream({
        onProgress: (data) => {
          console.log('Progress:', data);
          setSyncProgress(data.progress);
          setSyncingCurrent(data.current);
          setSyncingTotal(data.total);
          setSyncingTitle(data.title || t('common.initializing'));
        },
        onComplete: (data) => {
          console.log('Complete:', data);
          setSyncing(false);
          setSyncProgress(100);
          if (data.success > 0) {
            toast.success(t('settings.syncComplete'));
          }
          if (data.failed > 0) {
            toast.error(t('settings.syncError'));
          }
          loadData();
        },
        onError: (data) => {
          console.error('Sync error:', data);
          setSyncing(false);
          toast.error(t('settings.syncError'));
        },
      });

      setEventSource(connection);
    } catch (error) {
      console.error('Error starting sync:', error);
      setSyncing(false);
      toast.error(t('settings.syncError'));
    }
  };

  const cancelSync = () => {
    if (eventSource) {
      eventSource.close();
    }
    setSyncing(false);
    toast(t('common.cancel'));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Sync Modal */}
      {syncing && (
        <div className="desktop-sync-modal">
          <div className="desktop-sync-modal__overlay" onClick={cancelSync} />
          <div className="desktop-sync-modal__content">
            <div className="desktop-sync-modal__icon">
              <HiOutlineRefresh size={40} className="spinning" />
            </div>
            <h3 className="desktop-sync-modal__title">{t('settings.syncing')}</h3>
            <p className="desktop-sync-modal__series">{syncingTitle}</p>
            
            <div className="desktop-sync-modal__progress-bar">
              <div 
                className="desktop-sync-modal__progress-fill"
                style={{ width: `${syncProgress}%` }}
              />
            </div>
            
            <div className="desktop-sync-modal__stats">
              <span>{syncingCurrent}/{syncingTotal}</span>
              <span>{syncProgress}%</span>
            </div>
            
            <button 
              className="desktop-sync-modal__cancel"
              onClick={cancelSync}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>{t('admin.series')}</h1>
        <p>{t('settings.statistics')}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon purple">
            <HiOutlineUsers size={24} />
          </div>
          <h3>{stats?.total_users || 0}</h3>
          <p>{t('users.totalUsers')}</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon teal">
            <HiOutlineFilm size={24} />
          </div>
          <h3>{stats?.total_movies || 0}</h3>
          <p>{t('settings.totalMovies')}</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon pink">
            <HiOutlineVideoCamera size={24} />
          </div>
          <h3>{stats?.total_tv_shows || 0}</h3>
          <p>{t('settings.totalSeries')}</p>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon purple">
            <HiOutlineCollection size={24} />
          </div>
          <h3>{stats?.total_watchlist_items || 0}</h3>
          <p>{t('dashboard.totalTitles')}</p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>{t('settings.syncTVShows')}</h2>
        </div>

        <div style={{ padding: '16px' }}>
          <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
            {t('settings.syncDescription')}
          </p>

          <button
            type="button"
            onClick={handleSyncTVShows}
            className="btn btn-secondary"
            disabled={syncing || !keyStatus.configured}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: keyStatus.configured ? 'linear-gradient(135deg, var(--secondary), #0d9488)' : 'var(--background)',
              color: keyStatus.configured ? 'white' : 'var(--text-muted)',
              border: 'none',
            }}
          >
            {syncing ? (
              <>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                {t('settings.syncing')}
              </>
            ) : (
              <>
                <HiOutlineRefresh size={20} />
                {t('settings.syncTVShows')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminSeries;
